import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import { isKeychainProductId } from '../constants';

import SearchBar from '../components/Browser/SearchBar';
import Customise from '../components/Browser/Customise';
import CustomiseSkeleton from '../components/Browser/CustomiseSkeleton';
import ProductFilters from '../components/Browser/ProductFilters';
import SkinToneFilters from '../components/Navigation/SkinToneFilters';
import Products from '../components/Browser/Products';
import Start from '../components/Browser/Start';
import Navigation from '../containers/Navigation';

import { getProductSection } from '../utils/getProductSection';

import {
    customiseProduct,
    removeProduct,
    selectNamedProduct,
    selectProduct,
    selectSectionAndCategory,
    setCustomiseOverlayOpen,
    setSearchOpen,
} from '../actions';

const SearchToggleButton = ({
    setShowSearch,
    setSearchResults,
    setSearchTerm,
    setSearchPageInfo,
    setSearchSelectedProduct,
}) => {
    return (
        <button
            className="search-toggle-btn"
            onClick={() => {
                setShowSearch(true);
                setSearchResults(null);
                setSearchTerm('');
                setSearchPageInfo(null);
                setSearchSelectedProduct(null);
            }}
            aria-label="Open search"
            type="button"
        >
            <svg width="35" height="34" viewBox="0 0 35 34" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M31.9375 33.875L20.125 22.0625C19.1875 22.8125 18.1094 23.4063 16.8906 23.8438C15.6719 24.2813 14.375 24.5 13 24.5C9.59375 24.5 6.71094 23.3203 4.35156 20.9609C1.99219 18.6016 0.8125 15.7188 0.8125 12.3125C0.8125 8.90625 1.99219 6.02344 4.35156 3.66406C6.71094 1.30469 9.59375 0.125 13 0.125C16.4062 0.125 19.2891 1.30469 21.6484 3.66406C24.0078 6.02344 25.1875 8.90625 25.1875 12.3125C25.1875 13.6875 24.9688 14.9844 24.5313 16.2031C24.0938 17.4219 23.5 18.5 22.75 19.4375L34.5625 31.25L31.9375 33.875ZM13 20.75C15.3438 20.75 17.3359 19.9297 18.9766 18.2891C20.6172 16.6484 21.4375 14.6562 21.4375 12.3125C21.4375 9.96875 20.6172 7.97656 18.9766 6.33594C17.3359 4.69531 15.3438 3.875 13 3.875C10.6562 3.875 8.66406 4.69531 7.02344 6.33594C5.38281 7.97656 4.5625 9.96875 4.5625 12.3125C4.5625 14.6562 5.38281 16.6484 7.02344 18.2891C8.66406 19.9297 10.6562 20.75 13 20.75Z"
                    fill="#1C1B1F"
                />
            </svg>
        </button>
    );
};

SearchToggleButton.propTypes = {
    setShowSearch: PropTypes.func.isRequired,
    setSearchResults: PropTypes.func.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    setSearchPageInfo: PropTypes.func.isRequired,
    setSearchSelectedProduct: PropTypes.func.isRequired,
};

const Browser = ({
    currentBuildIndex,
    section = null,
    selectedProduct = null,
    products = [],
    selected,
    loading = false,
    loadingSection = null,
    finished = false,
    projectTitle = '',
    dispatch,
    skintone = '',
    errorOnBuy = false,
    setEnableBuilder = () => {},
    enableBuilder = true,
    isCustomiseOverlayOpen = false,
    onBrowserSurfaceClick = null,
    isSearchOpen = false,
}) => {
    const [showDescription, setShowDescription] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchPageInfo, setSearchPageInfo] = useState(null);
    const [searchSelectedProduct, setSearchSelectedProduct] = useState(null);
    const [pendingCustomiseId, setPendingCustomiseId] = useState(null);

    // Track last selected customise product id to aid recovery after state refreshes
    const lastCustomProductIdRef = useRef(null);
    useEffect(() => {
        if (selectedProduct?.id) {
            lastCustomProductIdRef.current = selectedProduct.id;
        }
        console.log('debugging browser:', selectedProduct?.id, lastCustomProductIdRef.current);
    }, [selectedProduct]);

    useEffect(() => {
        if (isCustomiseOverlayOpen && pendingCustomiseId && !selectedProduct && !loading && products?.length > 0) {
            const found = products.find((p) => (p?.node?.id || p?.id) === pendingCustomiseId);
            if (found) {
                const productObj = found.node || found;
                console.log('debugging browser:', { productObj });
                // Guard against rapid re-dispatch loops: only fire if different from last ref (or ref missing)
                if (lastCustomProductIdRef.current !== productObj.id) {
                    dispatch(customiseProduct(productObj));
                }
            }
        }
    }, [isCustomiseOverlayOpen, pendingCustomiseId, selectedProduct, loading, products, dispatch]);

    useEffect(() => {
        setShowDescription(false);
    }, [selectedProduct]);

    const handleSelect = useCallback(
        (product, attributes, addons, product_attribute, productFormJsonValues) =>
            dispatch(
                selectProduct(currentBuildIndex, product, attributes, addons, product_attribute, productFormJsonValues)
            ),
        [dispatch, currentBuildIndex]
    );

    const handleSelectNamed = useCallback(
        (
            currentBuildIndexParam,
            product,
            attributes,
            addons,
            product_attribute,
            productSection,
            productFormJsonValues
        ) =>
            dispatch(
                selectNamedProduct(
                    currentBuildIndexParam,
                    product,
                    attributes,
                    addons,
                    product_attribute,
                    productSection,
                    productFormJsonValues
                )
            ),
        [dispatch]
    );

    const handleRemove = useCallback(
        (sectionId) => dispatch(removeProduct(currentBuildIndex, sectionId)),
        [dispatch, currentBuildIndex]
    );

    const handleCustomise = useCallback(
        (product) => {
            setPendingCustomiseId(product.id);
            dispatch(setCustomiseOverlayOpen(true));
            dispatch(customiseProduct(product));
        },
        [dispatch]
    );

    const handleBrowserSurfaceClick = useCallback(
        (e) => {
            if (!onBrowserSurfaceClick) return;
            // Ignore clicks on/within navigation/search area
            if (e.target.closest('.navigation-search-container .navigation')) return;
            onBrowserSurfaceClick(e);
        },
        [onBrowserSurfaceClick]
    );

    const handleCloseSearch = () => {
        dispatch(setSearchOpen(false));
        setSearchSelectedProduct(null);
        dispatch(setCustomiseOverlayOpen(false));
    };

    const handleCustomiseProduct = (product) => {
        const productSection = getProductSection(product);
        const sectionConfig = {
            id: productSection,
            title: productSection.charAt(0).toUpperCase() + productSection.slice(1),
            emptyText: `Choose a ${productSection}`,
            handle:
                productSection === 'hatsAndHair'
                    ? 'hair-and-hats'
                    : productSection === 'heads'
                    ? 'all-heads-new-1'
                    : productSection === 'bodies'
                    ? 'all-torsos-new-website'
                    : productSection === 'legs'
                    ? 'all-legs'
                    : productSection === 'display'
                    ? 'all-displays'
                    : productSection === 'accessories'
                    ? 'all-accessories'
                    : productSection,
        };
        // Clear any previous selected browser product to avoid stale render
        dispatch(setCustomiseOverlayOpen(true));
        setPendingCustomiseId(product.id);
        setSearchSelectedProduct(product);
        dispatch(customiseProduct(product));
        dispatch(selectSectionAndCategory(sectionConfig, null, skintone));
    };

    const handleSelectInSearchCustomise = (product, attributes, addons, product_attribute, productFormJsonValues) => {
        const productSection = getProductSection(searchSelectedProduct);
        try {
            const sectionConfig = {
                id: productSection,
                title: productSection.charAt(0).toUpperCase() + productSection.slice(1),
                emptyText: `Choose a ${productSection}`,
                handle:
                    productSection === 'hatsAndHair'
                        ? 'hair-and-hats'
                        : productSection === 'heads'
                        ? 'all-heads-new-1'
                        : productSection === 'bodies'
                        ? 'all-torsos-new-website'
                        : productSection === 'legs'
                        ? 'all-legs'
                        : productSection === 'display'
                        ? 'all-displays'
                        : productSection === 'accessories'
                        ? 'all-accessories'
                        : productSection,
            };

            dispatch(selectSectionAndCategory(sectionConfig, null, skintone));

            dispatch(
                selectProduct(currentBuildIndex, product, attributes, addons, product_attribute, productFormJsonValues)
            );

            setSearchSelectedProduct(null);
            setPendingCustomiseId(null);
            dispatch(setCustomiseOverlayOpen(false));
        } catch (error) {
            console.error('Error selecting search product:', error);
            console.error('Error stack:', error.stack);
        }
    };

    if (finished && !errorOnBuy) {
        return null;
    }

    if (isSearchOpen) {
        const overlayActive = isCustomiseOverlayOpen || !!searchSelectedProduct;
        return (
            <div
                className={cn('browser browser-1', {
                    'search-active': isSearchOpen,
                    'product-overlay-active': overlayActive,
                })}
                onClick={handleBrowserSurfaceClick}
            >
                <div className="navigation-search-container search-title">
                    <h3>Search</h3>
                </div>
                <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchResults={searchResults || []}
                    setSearchResults={setSearchResults}
                    searchLoading={searchLoading}
                    setSearchLoading={setSearchLoading}
                    searchError={searchError}
                    setSearchError={setSearchError}
                    searchPageInfo={searchPageInfo}
                    setSearchPageInfo={setSearchPageInfo}
                    section={section}
                    selected={selected}
                    currentBuildIndex={currentBuildIndex}
                    projectTitle={projectTitle}
                    dispatch={dispatch}
                    selectProduct={selectProduct}
                    selectNamedProduct={selectNamedProduct}
                    removeProduct={removeProduct}
                    customiseProduct={handleCustomiseProduct}
                    skintone={skintone}
                    onCloseSearch={handleCloseSearch}
                />

                {searchSelectedProduct && (
                    <div className="searchCustomiseOverlay searchCustomiseOverlay-1">
                        {selectedProduct?.id === pendingCustomiseId ? (
                            !loading ? (
                                <Customise
                                    projectTitle={projectTitle}
                                    product={selectedProduct}
                                    selected={null}
                                    showDescription={showDescription}
                                    setShowDescription={setShowDescription}
                                    onSelect={handleSelectInSearchCustomise}
                                    onRemove={() => {
                                        setSearchSelectedProduct(null);
                                        setPendingCustomiseId(null);
                                        dispatch(setCustomiseOverlayOpen(false));
                                    }}
                                    backButtonFunc={() => {
                                        setSearchSelectedProduct(null);
                                        setPendingCustomiseId(null);
                                        dispatch(setCustomiseOverlayOpen(false));
                                    }}
                                />
                            ) : (
                                <CustomiseSkeleton
                                    product={selectedProduct}
                                    onClose={() => {
                                        dispatch(setCustomiseOverlayOpen(false));
                                        setPendingCustomiseId(null);
                                    }}
                                />
                            )
                        ) : null}
                    </div>
                )}
            </div>
        );
    }

    if (section) {
        // Special section: show skin tone selector in the grid area
        if (section === 'skinTone') {
            return (
                <div className="browser browser-2">
                    <div className="navigation-search-container">
                        <Navigation setEnableBuilder={setEnableBuilder} enableBuilder={enableBuilder} />
                        <SearchToggleButton
                            setShowSearch={(open) => dispatch(setSearchOpen(open))}
                            setSearchResults={setSearchResults}
                            setSearchTerm={setSearchTerm}
                            setSearchPageInfo={setSearchPageInfo}
                            setSearchSelectedProduct={setSearchSelectedProduct}
                        />
                    </div>
                    <div className="products-container large">
                        <ProductFilters />
                        <ul className="products products-3 le-products skintoneContainer">
                            <SkinToneFilters
                                projectTitle={projectTitle}
                                setEnableBuilder={setEnableBuilder}
                                titleText="Choose a skin tone"
                            />
                        </ul>
                    </div>
                </div>
            );
        }

        // Show products list; overlay Customise on top when selectedProduct exists
        if (products?.length > 0) {
            const selectedProductDetails =
                (selected[section] && selected[section].product?.id === selectedProduct?.id && selected[section]) ||
                (selected.keychain?.product?.id === selectedProduct?.id && selected.keychain) ||
                null;

            return (
                <div
                    className={cn('browser browser-3', { 'product-overlay-active': isCustomiseOverlayOpen })}
                    onClick={handleBrowserSurfaceClick}
                >
                    <div className="navigation-search-container">
                        <Navigation setEnableBuilder={setEnableBuilder} enableBuilder={enableBuilder} />
                        <SearchToggleButton
                            setShowSearch={(open) => dispatch(setSearchOpen(open))}
                            setSearchResults={setSearchResults}
                            setSearchTerm={setSearchTerm}
                            setSearchPageInfo={setSearchPageInfo}
                            setSearchSelectedProduct={setSearchSelectedProduct}
                        />
                    </div>
                    <ProductFilters />
                    <Products
                        products={products}
                        section={section}
                        selectedProducts={selected}
                        selected={selected[section]}
                        onSelect={handleSelect}
                        onSelectNamedProduct={handleSelectNamed}
                        currentBuildIndex={currentBuildIndex}
                        projectTitle={projectTitle}
                        onRemove={handleRemove}
                        onCustomise={handleCustomise}
                        skintone={skintone}
                        // Prefetch: dispatch customiseProduct but don't open overlay yet
                        prefetchProductDetails={(p) => {
                            if (!p?.id) return;
                            if (p.id === selectedProduct?.id) return;
                            dispatch(customiseProduct(p, { silent: true }));
                        }}
                    />

                    {isCustomiseOverlayOpen && (
                        <div className="searchCustomiseOverlay searchCustomiseOverlay-2">
                            {(() => {
                                // Allow direct launches (e.g. keychain preview click) where we may not have set pendingCustomiseId.
                                // Simplify: if a selectedProduct exists and overlay flag is true, render customise.
                                const allowDirect = !!selectedProduct;
                                if (!allowDirect) return null;
                                return !loading ? (
                                    <Customise
                                        projectTitle={projectTitle}
                                        product={selectedProduct}
                                        selected={selectedProductDetails}
                                        showDescription={showDescription}
                                        setShowDescription={setShowDescription}
                                        onSelect={(
                                            product,
                                            attributes,
                                            addons,
                                            product_attribute,
                                            productFormJsonValues
                                        ) =>
                                            dispatch(
                                                selectProduct(
                                                    currentBuildIndex,
                                                    product,
                                                    attributes,
                                                    addons,
                                                    product_attribute,
                                                    productFormJsonValues
                                                )
                                            )
                                        }
                                        onRemove={() => {
                                            // If current product is the keychain, remove from keychain slot even if section !== 'keychain'
                                            const isKeychainProduct = isKeychainProductId(selectedProduct?.id);
                                            const targetSection = isKeychainProduct ? 'keychain' : section;
                                            dispatch(removeProduct(currentBuildIndex, targetSection));
                                            dispatch(setCustomiseOverlayOpen(false));
                                            setPendingCustomiseId(null);
                                        }}
                                        backButtonFunc={() => {
                                            dispatch(setCustomiseOverlayOpen(false));
                                            setPendingCustomiseId(null);
                                        }}
                                    />
                                ) : (
                                    <CustomiseSkeleton
                                        product={selectedProduct}
                                        onClose={() => {
                                            dispatch(setCustomiseOverlayOpen(false));
                                            setPendingCustomiseId(null);
                                        }}
                                    />
                                );
                            })()}
                        </div>
                    )}
                </div>
            );
        }

        // viewing categories within a section
        return (
            <div className="browser browser-4" onClick={handleBrowserSurfaceClick}>
                <div className="navigation-search-container">
                    <Navigation setEnableBuilder={setEnableBuilder} enableBuilder={enableBuilder} />
                    <SearchToggleButton
                        setShowSearch={(open) => dispatch(setSearchOpen(open))}
                        setSearchResults={setSearchResults}
                        setSearchTerm={setSearchTerm}
                        setSearchPageInfo={setSearchPageInfo}
                        setSearchSelectedProduct={setSearchSelectedProduct}
                    />
                </div>
                <div className="products-container">
                    <ProductFilters />
                    {(() => {
                        // Treat an active customise overlay launch (e.g. keychain direct open) with zero products as a loading state
                        const awaitingInitialProducts =
                            isCustomiseOverlayOpen && products?.length === 0 && !!loadingSection;
                        const showLoading = loading || awaitingInitialProducts;
                        const loadingImage =
                            'https://cdn.shopify.com/s/files/1/0223/5446/0736/files/loading-gif.gif?v=1760447109';
                        return (
                            <p
                                className={cn('infoText', {
                                    showLoading,
                                })}
                            >
                                {showLoading ? (
                                    <img
                                        src={loadingImage}
                                        alt={`Building ${loadingSection ? loadingSection : 'figures'}...`}
                                    />
                                ) : (
                                    'Sorry, there are no products matching these filters.'
                                )}
                            </p>
                        );
                    })()}
                </div>
            </div>
        );
    }

    // show the start screen
    return (
        <div className="browser browser-5">
            <Navigation setEnableBuilder={setEnableBuilder} enableBuilder={enableBuilder} />
            <Start
                onStart={() =>
                    dispatch(
                        selectSectionAndCategory({
                            id: 'skinTone',
                            title: 'Skin tone',
                            emptyText: 'Choose a skin tone',
                            handle: null,
                        })
                    )
                }
            />
        </div>
    );
};

const productCoreShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    collections: PropTypes.shape({
        edges: PropTypes.arrayOf(
            PropTypes.shape({
                node: PropTypes.shape({
                    title: PropTypes.string,
                    handle: PropTypes.string,
                }),
            })
        ),
    }),
    tags: PropTypes.arrayOf(PropTypes.string),
});

const productEdgeShape = PropTypes.shape({
    node: productCoreShape.isRequired,
    cursor: PropTypes.string,
    boldProductOptions: PropTypes.any,
    _isRelated: PropTypes.bool,
});

const productListItemShape = PropTypes.oneOfType([productCoreShape, productEdgeShape]);

Browser.propTypes = {
    currentBuildIndex: PropTypes.number.isRequired,
    section: PropTypes.string,
    selectedProduct: productCoreShape,
    products: PropTypes.arrayOf(productListItemShape),
    selected: PropTypes.shape({
        accessories: PropTypes.arrayOf(
            PropTypes.shape({
                product: productCoreShape,
            })
        ),
        hatsAndHair: PropTypes.shape({ product: productCoreShape }),
        heads: PropTypes.shape({ product: productCoreShape }),
        bodies: PropTypes.shape({ product: productCoreShape }),
        legs: PropTypes.shape({ product: productCoreShape }),
        display: PropTypes.shape({ product: productCoreShape }),
        keychain: PropTypes.shape({ product: productCoreShape }),
        skinTone: PropTypes.shape({ product: productCoreShape }),
    }).isRequired,
    loading: PropTypes.bool,
    loadingSection: PropTypes.string,
    finished: PropTypes.bool,
    projectTitle: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    skintone: PropTypes.string,
    errorOnBuy: PropTypes.bool,
    setEnableBuilder: PropTypes.func,
    enableBuilder: PropTypes.bool,
    isCustomiseOverlayOpen: PropTypes.bool,
    onBrowserSurfaceClick: PropTypes.func,
    isSearchOpen: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    ...state.browser,
    currentBuildIndex: state.currentBuildIndex,
    section: state.navigation.selected,
    selected: state.builds[state.currentBuildIndex].selected,
    loading: state.loading,
    loadingSection: state.loadingSection,
    finished: state.finished,
    errorOnBuy: state.errorOnBuy,
    projectTitle: state.builds[state.currentBuildIndex].projectTitle,
    skintone: state.builds[state.currentBuildIndex].skintone,
    isCustomiseOverlayOpen: state.isCustomiseOverlayOpen,
    isSearchOpen: state.isSearchOpen,
});

export default connect(mapStateToProps)(Browser);
